import React, { useState } from 'react';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { getUserInfo } from '../../utils/accessControl';
import AddressAutocomplete from '../common/AddressAutocomplete';
import './TransactionProperties.css';

// Manual "Add Property" popup for the Selected Properties tab (Buying
// transactions). Saves through the SAME endpoint (POST .../interested-properties)
// that MLS-search and chat-shared properties already use — see
// PropertiesPage.js's markInterestedFromFavorite for the reference payload
// shape this mirrors. No mlsId is ever sent: the field is optional
// server-side specifically for this flow (see AddInterestedPropertyRequest),
// so a manually-typed property has no MLS number at all, real or synthetic.
//
// `editingProperty` (when set) puts the modal in Edit mode: there is no
// dedicated update endpoint, so saving an edit deletes the old row and posts
// a new one — both endpoints already exist and are already exercised by the
// existing Remove/Add paths. The backend already refuses to delete a
// property once an RPA has been created on it (removeInterestedProperty),
// so this can never silently orphan RPA state; TransactionProperties only
// offers Edit for rows where rpaCreated is falsy in the first place.
const AddPropertyModal = ({ transaction, editingProperty, getFirebaseToken, onClose, onSaved }) => {
  const isEdit = !!editingProperty;

  const [street, setStreet] = useState(editingProperty?.propertyAddress || '');
  const [city, setCity] = useState(editingProperty?.propertyCity || '');
  const [state, setState] = useState(editingProperty?.propertyState || '');
  const [zip, setZip] = useState(editingProperty?.propertyZip || '');
  const [price, setPrice] = useState(
    editingProperty?.listPrice != null ? String(editingProperty.listPrice) : ''
  );
  const [beds, setBeds] = useState(
    editingProperty?.bedrooms != null ? String(editingProperty.bedrooms) : ''
  );
  const [baths, setBaths] = useState(
    editingProperty?.bathrooms != null ? String(editingProperty.bathrooms) : ''
  );
  const [notes, setNotes] = useState(editingProperty?.description || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Restrict autocomplete suggestions to the viewer's licensed state, same
  // pattern as PurchaseTransactionPage.js — best-effort, falls back to
  // nationwide suggestions when unavailable rather than blocking entry.
  const restrictToStateCode = (() => {
    const user = getUserInfo();
    const raw = user?.licenseState || user?.stateCode || user?.state || '';
    return raw ? String(raw).toUpperCase() : undefined;
  })();

  const handleAddressSelect = (parsed) => {
    if (parsed.street) setStreet(parsed.street);
    if (parsed.city) setCity(parsed.city);
    if (parsed.stateCode) setState(parsed.stateCode);
    if (parsed.zip) setZip(parsed.zip);
  };

  const isValid = street.trim() && city.trim() && state.trim() && zip.trim();

  const parsePrice = (val) => {
    if (!val || !val.trim()) return undefined;
    const num = Number(val.replace(/[^0-9.]/g, ''));
    return Number.isFinite(num) && num > 0 ? num : undefined;
  };
  const parseIntOrUndefined = (val) => {
    if (!val || !val.trim()) return undefined;
    const num = parseInt(val, 10);
    return Number.isFinite(num) && num >= 0 ? num : undefined;
  };
  const parseFloatOrUndefined = (val) => {
    if (!val || !val.trim()) return undefined;
    const num = parseFloat(val);
    return Number.isFinite(num) && num >= 0 ? num : undefined;
  };

  const handleSave = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    setError('');
    try {
      const firebaseToken = await getFirebaseToken();
      const headers = { Authorization: `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };

      if (isEdit) {
        // No update endpoint exists — remove the old row first. The backend
        // itself refuses this if an RPA now exists on the property (race
        // condition guard), in which case we stop here without posting a
        // replacement, so nothing is lost.
        await apiClient.delete(
          API_ENDPOINTS.deleteInterestedProperty(transaction.id, editingProperty.id),
          { headers }
        );
      }

      const payload = {
        propertyAddress: street.trim(),
        propertyCity: city.trim(),
        propertyState: state.trim().toUpperCase(),
        propertyZip: zip.trim(),
        listPrice: parsePrice(price),
        bedrooms: parseIntOrUndefined(beds),
        bathrooms: parseFloatOrUndefined(baths),
        description: notes.trim() || undefined,
        source: 'MANUAL',
      };

      await apiClient.post(API_ENDPOINTS.interestedProperties(transaction.id), payload, { headers });
      onSaved();
    } catch (err) {
      console.error('Failed to save manually-added property:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save property. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="prop-modal-overlay" onClick={onClose}>
      <div className="prop-add-modal" onClick={e => e.stopPropagation()}>
        <div className="prop-rpa-modal-header">
          <h3 className="prop-rpa-modal-title" style={{ textAlign: 'left' }}>
            {isEdit ? 'Edit Property' : 'Add Property Manually'}
          </h3>
          <button className="prop-rpa-modal-close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="prop-add-modal-body">
          <p className="prop-add-modal-sub">
            Add a property that wasn't found through search or shared via chat.
          </p>

          <div className="prop-add-field-group">
            <label className="prop-add-label">Street Address <span className="prop-add-required">*</span></label>
            <AddressAutocomplete
              value={street}
              onChange={setStreet}
              onAddressSelect={handleAddressSelect}
              placeholder="e.g. 4321 E Mission Avenue"
              inputClassName="prop-add-input"
              restrictToStateCode={restrictToStateCode}
            />
          </div>

          <div className="prop-add-field-row">
            <div className="prop-add-field-group">
              <label className="prop-add-label">City <span className="prop-add-required">*</span></label>
              <input
                type="text"
                className="prop-add-input"
                value={city}
                onChange={e => setCity(e.target.value)}
                placeholder="e.g. Merced"
              />
            </div>
            <div className="prop-add-field-group">
              <label className="prop-add-label">State <span className="prop-add-required">*</span></label>
              <input
                type="text"
                className="prop-add-input"
                value={state}
                onChange={e => setState(e.target.value)}
                placeholder="e.g. CA"
                maxLength={2}
              />
            </div>
          </div>

          <div className="prop-add-field-group">
            <label className="prop-add-label">Zip Code <span className="prop-add-required">*</span></label>
            <input
              type="text"
              className="prop-add-input"
              value={zip}
              onChange={e => setZip(e.target.value)}
              placeholder="e.g. 95341"
            />
          </div>

          <div className="prop-add-field-row">
            <div className="prop-add-field-group">
              <label className="prop-add-label">Price <span className="prop-add-optional">(optional)</span></label>
              <input
                type="text"
                className="prop-add-input"
                value={price}
                onChange={e => setPrice(e.target.value)}
                placeholder="$"
                inputMode="decimal"
              />
            </div>
            <div className="prop-add-field-group">
              <label className="prop-add-label">Beds <span className="prop-add-optional">(optional)</span></label>
              <input
                type="text"
                className="prop-add-input"
                value={beds}
                onChange={e => setBeds(e.target.value)}
                placeholder="e.g. 3"
                inputMode="numeric"
              />
            </div>
            <div className="prop-add-field-group">
              <label className="prop-add-label">Baths <span className="prop-add-optional">(optional)</span></label>
              <input
                type="text"
                className="prop-add-input"
                value={baths}
                onChange={e => setBaths(e.target.value)}
                placeholder="e.g. 2"
                inputMode="decimal"
              />
            </div>
          </div>

          <div className="prop-add-field-group">
            <label className="prop-add-label">Notes <span className="prop-add-optional">(optional)</span></label>
            <textarea
              className="prop-add-input"
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Anything worth remembering about this property"
            />
          </div>

          {error && <p className="prop-add-error">{error}</p>}

          <div className="prop-add-modal-actions">
            <button className="prop-add-cancel-btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="prop-add-save-btn" onClick={handleSave} disabled={!isValid || saving}>
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add to Selected Properties')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPropertyModal;
